import { useEffect, useState } from 'react';
import { api, ErreurApi } from '../api.js';
import { tokens } from '../tokens.js';
import {
  Badge, BoiteErreur, labelChamp, inputBase,
  boutonPrincipal, boutonSecondaire,
} from '../helpers.jsx';
import { Plus, Trash2, Edit, Save, X, Layers } from 'lucide-react';

// CRUD services hierarchique (2 niveaux : service / sous-service).
export function AdminServices() {
  const [services, setServices]   = useState([]);
  const [applications, setApps]   = useState([]);
  const [chargement, setCharg]    = useState(true);
  const [erreur, setErreur]       = useState('');
  // Modale de creation
  const [creerOuvert, setCreer]   = useState(false);
  const [creerParent, setCreerParent] = useState(null);
  // Edition inline
  const [editId, setEditId]       = useState(null);

  function recharger() {
    setCharg(true);
    Promise.all([
      api.get('/admin/services').then(d => setServices(d.services || [])),
      api.get('/admin/applications').then(d => setApps(d.applications || [])).catch(() => {}),
    ])
      .catch(e => setErreur(e.message))
      .finally(() => setCharg(false));
  }
  useEffect(() => { recharger(); }, []);

  async function creer(payload) {
    try {
      await api.post('/admin/services', payload);
      setCreer(false);
      setCreerParent(null);
      recharger();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Erreur');
    }
  }

  async function modifier(id, payload) {
    try {
      await api.put(`/admin/services/${id}`, payload);
      setEditId(null);
      recharger();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Erreur');
    }
  }

  async function desactiver(id) {
    if (!confirm("Désactiver ce service ? Les sous-services seront aussi désactivés. Les tickets existants restent.")) return;
    try {
      await api.delete(`/admin/services/${id}`);
      recharger();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Erreur');
    }
  }

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 18 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: tokens.ink }}>
            Services & sous-services
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: tokens.inkSoft }}>
            Catégories que les utilisateurs choisissent quand ils créent un ticket.
          </p>
        </div>
        <button onClick={() => { setCreerParent(null); setCreer(true); }} style={boutonPrincipal}>
          <Plus size={16} /> Nouveau service
        </button>
      </div>

      {erreur && <BoiteErreur message={erreur} />}
      {chargement && <div style={etat}>Chargement…</div>}

      {!chargement && services.length === 0 && (
        <div style={etat}>
          <Layers size={28} color={tokens.inkMuted} />
          <div style={{ marginTop: 10 }}>Aucun service configuré.</div>
          <div style={{ marginTop: 4, fontSize: 12 }}>
            Crée au moins un service pour que les utilisateurs puissent saisir des tickets.
          </div>
        </div>
      )}

      <div style={{ display:'flex', flexDirection:'column', gap: 10 }}>
        {services.map(s => (
          <CarteService
            key={s.id}
            service={s}
            applications={applications}
            allServices={services}
            estEdition={editId === s.id}
            onEdit={() => setEditId(s.id)}
            onAnnulerEdit={() => setEditId(null)}
            onSauver={(payload) => modifier(s.id, payload)}
            onDesactiver={() => desactiver(s.id)}
            onAjouterSous={() => { setCreerParent(s.id); setCreer(true); }}
            onEditSous={(id) => setEditId(id)}
            editIdSous={editId}
            onAnnulerEditSous={() => setEditId(null)}
            onSauverSous={(id, p) => modifier(id, p)}
            onDesactiverSous={(id) => desactiver(id)}
          />
        ))}
      </div>

      {creerOuvert && (
        <ModaleCreer
          parentId={creerParent}
          services={services}
          applications={applications}
          onAnnuler={() => { setCreer(false); setCreerParent(null); }}
          onValider={creer}
        />
      )}
    </div>
  );
}

function CarteService({
  service, applications, allServices,
  estEdition, onEdit, onAnnulerEdit, onSauver, onDesactiver,
  onAjouterSous, editIdSous, onEditSous, onAnnulerEditSous, onSauverSous, onDesactiverSous,
}) {
  return (
    <div style={{
      background: tokens.surface,
      border: `1px solid ${tokens.border}`,
      borderRadius: 10,
      padding: 14,
      opacity: service.actif ? 1 : 0.5,
    }}>
      {estEdition ? (
        <FormulaireService
          initial={service}
          applications={applications}
          onAnnuler={onAnnulerEdit}
          onValider={onSauver}
        />
      ) : (
        <div style={{ display:'flex', alignItems:'center', gap: 10 }}>
          <Layers size={16} color={tokens.accent} />
          <strong style={{ fontSize: 14, color: tokens.ink }}>{service.nom}</strong>
          {service.application_nom && (
            <Badge bg={tokens.surfaceAlt} fg={tokens.inkSoft} taille="petit">
              {service.application_nom}
            </Badge>
          )}
          {!service.actif && (
            <Badge bg={tokens.dangerBg} fg={tokens.danger} taille="petit">Inactif</Badge>
          )}
          <div style={{ marginLeft:'auto', display:'flex', gap: 6 }}>
            <button onClick={onAjouterSous}
              style={{ ...boutonSecondaire, padding: '4px 10px', fontSize: 12 }}
              title="Ajouter un sous-service"
            >
              <Plus size={12} /> Sous-service
            </button>
            <button onClick={onEdit}
              style={{ ...boutonSecondaire, padding: '4px 8px', fontSize: 12 }}
              title="Modifier"
            ><Edit size={12} /></button>
            {service.actif && (
              <button onClick={onDesactiver}
                style={{ ...boutonSecondaire, padding: '4px 8px', fontSize: 12, color: tokens.danger }}
                title="Désactiver"
              ><Trash2 size={12} /></button>
            )}
          </div>
        </div>
      )}

      {service.sous_services?.length > 0 && (
        <div style={{ marginTop: 10, marginLeft: 24, display:'flex', flexDirection:'column', gap: 4 }}>
          {service.sous_services.map(ss => (
            <div key={ss.id} style={{
              display:'flex', alignItems:'center', gap: 10,
              padding: '6px 10px',
              background: tokens.bg, borderRadius: 6,
              opacity: ss.actif ? 1 : 0.5,
            }}>
              {editIdSous === ss.id ? (
                <FormulaireService
                  initial={ss}
                  applications={applications}
                  onAnnuler={onAnnulerEditSous}
                  onValider={(p) => onSauverSous(ss.id, p)}
                  petit
                />
              ) : (
                <>
                  <span style={{ fontSize: 13, color: tokens.ink }}>↳ {ss.nom}</span>
                  {!ss.actif && (
                    <Badge bg={tokens.dangerBg} fg={tokens.danger} taille="petit">Inactif</Badge>
                  )}
                  <div style={{ marginLeft:'auto', display:'flex', gap: 4 }}>
                    <button onClick={() => onEditSous(ss.id)}
                      style={{ background:'transparent', border:'none', cursor:'pointer', color: tokens.inkSoft }}
                    ><Edit size={11} /></button>
                    {ss.actif && (
                      <button onClick={() => onDesactiverSous(ss.id)}
                        style={{ background:'transparent', border:'none', cursor:'pointer', color: tokens.danger }}
                      ><Trash2 size={11} /></button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FormulaireService({ initial, applications, onAnnuler, onValider, petit = false }) {
  const [nom, setNom] = useState(initial.nom || '');
  const [appId, setAppId] = useState(initial.application_id ? String(initial.application_id) : '');
  const [actif, setActif] = useState(initial.actif !== false);

  return (
    <div style={{ display:'flex', alignItems:'center', gap: 8, flex: 1 }}>
      <input
        autoFocus
        value={nom}
        onChange={(e) => setNom(e.target.value)}
        style={{ ...inputBase, flex: 1, padding: petit ? '4px 8px' : '6px 10px' }}
        placeholder="Nom"
      />
      {!petit && !initial.parent_id && (
        <select
          value={appId}
          onChange={(e) => setAppId(e.target.value)}
          style={{ ...inputBase, width: 200, padding: '6px 10px' }}
        >
          <option value="">— Application —</option>
          {applications.map(a => (
            <option key={a.id} value={a.id}>{a.nom}</option>
          ))}
        </select>
      )}
      <label style={{ fontSize: 11, color: tokens.inkSoft, display:'flex', alignItems:'center', gap: 4 }}>
        <input type="checkbox" checked={actif} onChange={(e) => setActif(e.target.checked)} />
        Actif
      </label>
      <button
        onClick={() => onValider({ nom: nom.trim(), application_id: appId || null, actif })}
        style={{ ...boutonPrincipal, padding: petit ? '4px 8px' : '6px 10px', fontSize: 12 }}
        disabled={nom.trim() === ''}
      >
        <Save size={12} />
      </button>
      <button onClick={onAnnuler}
        style={{ ...boutonSecondaire, padding: petit ? '4px 8px' : '6px 10px', fontSize: 12 }}
      ><X size={12} /></button>
    </div>
  );
}

function ModaleCreer({ parentId, services, applications, onAnnuler, onValider }) {
  const [nom, setNom] = useState('');
  const [appId, setAppId] = useState('');
  const parent = parentId ? services.find(s => s.id === parentId) : null;

  return (
    <div style={{
      position:'fixed', inset: 0, zIndex: 50,
      background: 'rgba(0,0,0,0.5)',
      display:'flex', alignItems:'center', justifyContent:'center', padding: 20,
    }}>
      <div style={{
        background: tokens.surface, borderRadius: 12,
        maxWidth: 420, width: '100%', padding: 24,
        boxShadow: '0 10px 40px rgba(0,0,0,0.20)',
      }}>
        <h3 style={{ margin: '0 0 14px', fontSize: 16, fontWeight: 600 }}>
          {parent ? `Nouveau sous-service dans "${parent.nom}"` : 'Nouveau service'}
        </h3>

        <label style={labelChamp}>Nom</label>
        <input
          autoFocus
          value={nom}
          onChange={(e) => setNom(e.target.value)}
          style={inputBase}
          placeholder={parent ? 'Ex : Module factures' : 'Ex : Dolibarr'}
        />

        {!parent && (
          <>
            <label style={{ ...labelChamp, marginTop: 14 }}>Application liée (optionnel)</label>
            <select
              value={appId}
              onChange={(e) => setAppId(e.target.value)}
              style={inputBase}
            >
              <option value="">— Aucune —</option>
              {applications.map(a => (
                <option key={a.id} value={a.id}>{a.nom}</option>
              ))}
            </select>
          </>
        )}

        <div style={{ display:'flex', gap: 8, justifyContent:'flex-end', marginTop: 18 }}>
          <button onClick={onAnnuler} style={boutonSecondaire}>Annuler</button>
          <button
            onClick={() => onValider({
              nom: nom.trim(),
              parent_id: parentId,
              application_id: !parent && appId ? Number(appId) : null,
            })}
            disabled={nom.trim() === ''}
            style={boutonPrincipal}
          >Créer</button>
        </div>
      </div>
    </div>
  );
}

const etat = {
  background: tokens.surface,
  border: `1px solid ${tokens.border}`,
  borderRadius: 10,
  padding: 32,
  textAlign:'center',
  color: tokens.inkSoft,
  fontSize: 14,
};
